import { Trans, useLingui } from '@lingui/react/macro';
import { SportType } from 'modules/championship/ui/components/form-parts/rules/types';
import React, { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import UDFormDateRange from 'ud-form/components/datetime/range';
import { UDFormLabeledCheckbox, UDFormLabeledInput } from 'ud-form/components';

interface ApplicationCompanyBaseProps {
  sportType?: SportType;
  children?: ReactNode;
}

const ApplicationCompanyBase: React.FC<ApplicationCompanyBaseProps> = ({ sportType = 'general', children }) => {
  const { t } = useLingui();
  const location = useLocation();
  const highlight = location.state?.highlight || {};

  return (
    <div className="row mt-5 gap-6">
      <div className="w-1/2">
        <UDFormDateRange
          startName="settings.applicationStartDate"
          endName="settings.applicationEndDate"
          startLabel={t`Начало приема заявок`}
          endLabel={t`Окончание приема заявок`}
          containerProps={{ className: 'mb-5' }}
          highlightStart={highlight?.applicationStartDate}
          highlightEnd={highlight?.applicationEndDate}
          startTooltip={{
            tooltip: t`Дата, когда организатор начинает принимать заявки на участие в турнире. До этого момента команды не смогут зарегистрироваться.`,
            tooltipProps: {
              position: 'input-right',
            },
          }}
          endTooltip={{
            tooltip: t`Дата, после которой нельзя будет отправлять или редактировать заявки. Желательно установить с некоторым опережением перед началом турнира.`,
            tooltipProps: {
              position: 'input-right',
            },
          }}
        />

        <UDFormLabeledInput
          name="settings.applicationMinPlayers"
          placeholder={t`0 чел`}
          label={t`Минимальное количество игроков в заявке`}
          tooltip={
            <div>
              <Trans>
                Число игроков, необходимое для подтверждения заявки команды. Например, если указано "5", команда не сможет участвовать,
                пока не добавит 5 игроков.
              </Trans>
              <br />
              <br />
              <Trans>Значение "0" означает отсутствие ограничений.</Trans>
            </div>
          }
          tooltipProps={{
            position: 'input-right',
          }}
        />

        <UDFormLabeledInput
          name="settings.applicationMaxPlayers"
          placeholder={t`0 чел`}
          label={t`Максимальное количество игроков в заявке`}
          tooltip={
            <div>
              <Trans>
                Лимит заявленных игроков, которые могут быть в составе команды. Например, "10" – даже если у команды больше участников,
                заявить можно будет только 10 игроков.
              </Trans>
              <br />
              <br />
              <Trans>При значении "0" ограничение 28 игроков.</Trans>
            </div>
          }
          tooltipProps={{
            position: 'input-right',
          }}
        />

        <UDFormLabeledInput
          name="settings.participationFee"
          placeholder="0 ₽"
          label={t`Взнос за участие`}
          tooltip={
            <div>
              <Trans>
                Сумма, которую должна оплатить команда для участия в турнире. Если указано "0 ₽", участие бесплатное. Данный параметр
                будет показываться в описании к турниру для участников.
              </Trans>
              <br />
              <br />
              <Trans>
                ⚠️Важно: Параметр несет исключительно информативный характер, платформа не принимает никакие платежи от участников
                турнира.
              </Trans>
            </div>
          }
          tooltipProps={{
            position: 'input-right',
          }}
        />

        <UDFormLabeledCheckbox
          name="settings.applicationTransfersAllowed"
          label={t`Переходы возможны`}
          tooltip={
            <div>
              <Trans>
                Позволяет участникам менять команды после подачи запроса (например, если в турнире предусмотрена возможность "дозаявок"
                или замен).
              </Trans>
              <br />
              <br />
              <Trans>Если опция выключена – состав команды фиксируется после окончания заявочной кампании.</Trans>
            </div>
          }
          tooltipProps={{
            position: 'input-right',
          }}
        />

        {children}
      </div>
    </div>
  );
};

export default ApplicationCompanyBase;
